import { useEffect, useState } from 'react';
import { interpretationsApi } from '../services/api';
import { CompanyOption, InterpretationStatus } from '../types';

/**
 * The single seat of the company dropdown's data (E06-F02).
 *
 * The options used to come from a page sweep of `/interpretations/all` -- ten
 * requests of a hundred, 697 of 976 records covered, and a footer admitting it. Now
 * the SERVER walks its own light index and answers ONCE with every distinct company
 * and how many entries name it, so this hook moves exactly one request per
 * `(enabled, status)` and nothing else: no loop, no cap, no filtering.
 *
 * It has the shape of `useInterpretationSearch`, not of the sweep it replaces.
 */

export interface CompaniesState {
  /** `null` = nothing asked yet, or it failed. `[]` = asked, and there are none. */
  companies: CompanyOption[] | null;
  loading: boolean;
  failed: boolean;
}

const OFF: CompaniesState = { companies: null, loading: false, failed: false };

/**
 * Fetches the companies of the index while `enabled` holds.
 *
 * @param enabled Whether the screen needs the list at all -- the auditor opened the
 *                menu or has a company chosen. `false` emits NO request: a session
 *                that never touches the control pays nothing (R10).
 * @param status The list's status filter, honoured by the request; `'all'` is the
 *               ABSENCE of the parameter, never the literal string.
 *
 * Deps are `[enabled, status]` and nothing else: reopening the menu or typing a term
 * is served from this state (the cache is the state itself while the screen lives),
 * and changing the status asks again. The cleanup marks the previous request
 * `cancelled`, so an answer to a status the auditor already left cannot repaint the
 * newer one. A rejected request -- a 404 from a backend without the route included --
 * or a body whose `companies` is not a list (the Jest automock's `undefined`) is a
 * FAILURE: `companies` stays `null`, never `[]`. "We could not ask" and "there are
 * none" are two different sentences on screen.
 */
export function useCompanies(
  enabled: boolean,
  status: InterpretationStatus | 'all'
): CompaniesState {
  const [state, setState] = useState<CompaniesState>(OFF);

  useEffect(() => {
    if (!enabled) {
      setState(OFF);
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, failed: false }));

    const run = async () => {
      try {
        const response = await interpretationsApi.companies({
          status: status === 'all' ? undefined : status,
        });
        if (!response || !Array.isArray(response.companies)) {
          throw new Error('companies response without a list');
        }
        if (!cancelled) {
          setState({ companies: response.companies, loading: false, failed: false });
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        if (!cancelled) {
          setState({ companies: null, loading: false, failed: true });
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [enabled, status]);

  return state;
}
